import React, { useCallback, useState } from 'react';

import type { AuthConfig } from 'model/authConfig';
import {
  addAuthConfig,
  getActiveConfig,
  loadAuthConfigs,
  removeAuthConfig,
  saveActiveConfigName,
} from 'model/authConfig';
import { useAuthConfigForm } from 'hooks/useAuthConfigForm';
import Styled from './AuthConfigManager.styled';

const {
  StyledRoot,
  StyledHeading,
  StyledSection,
  StyledFieldLabel,
  StyledSelectorRow,
  StyledConfigSelect,
  StyledEmptyHint,
  StyledEditorCard,
  StyledEditorTitle,
  StyledFieldStack,
  StyledField,
  StyledTextInput,
  StyledButtonRow,
  StyledEditHint,
} = Styled;

interface OwnProps {
  onConfigSelected: (config: AuthConfig) => void;
}

/**
 * Manages saved environment auth configs (stored in localStorage). Owns
 * the selector dropdown + the add/edit form. Form state lives in the
 * reducer-backed `useAuthConfigForm`; editor-mode flags stay as plain
 * useStates because they're three loosely-coupled UI toggles, not a
 * single coherent state.
 */
const AuthConfigManager = ({ onConfigSelected }: OwnProps) => {
  const [configs, setConfigs] = useState<AuthConfig[]>(loadAuthConfigs());
  const [activeConfigName, setActiveConfigName] = useState<string>(getActiveConfig()?.name ?? '');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingConfigName, setEditingConfigName] = useState<string | null>(null);

  const { state: form, setField, reset, loadFromConfig } = useAuthConfigForm();

  const handleSelectConfig = useCallback(
    (name: string) => {
      const config = configs.find((c) => c.name === name);
      if (!config) return;
      setActiveConfigName(name);
      saveActiveConfigName(name);
      onConfigSelected(config);
    },
    [configs, onConfigSelected],
  );

  const handleChangeSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      handleSelectConfig(event.target.value);
    },
    [handleSelectConfig],
  );

  const handleChangeName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setField('name', event.target.value),
    [setField],
  );
  const handleChangeApiBaseUrl = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setField('apiBaseUrl', event.target.value),
    [setField],
  );
  const handleChangeClientId = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setField('clientId', event.target.value),
    [setField],
  );
  const handleChangeClientSecret = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setField('clientSecret', event.target.value),
    [setField],
  );

  const handleAddNew = useCallback(() => {
    setIsAddingNew(true);
    setIsEditing(false);
    setEditingConfigName(null);
    reset();
  }, [reset]);

  const handleEdit = useCallback(
    (configName: string) => {
      const config = configs.find((c) => c.name === configName);
      if (!config) return;
      setIsEditing(true);
      setIsAddingNew(true);
      setEditingConfigName(configName);
      loadFromConfig(config);
    },
    [configs, loadFromConfig],
  );

  const handleEditActive = useCallback(() => {
    if (activeConfigName) handleEdit(activeConfigName);
  }, [activeConfigName, handleEdit]);

  const handleSaveNew = useCallback(() => {
    if (
      !form.name.trim() ||
      !form.apiBaseUrl.trim() ||
      !form.clientId.trim() ||
      !form.clientSecret.trim()
    ) {
      window.alert('All fields are required');
      return;
    }

    if (isEditing && editingConfigName && editingConfigName !== form.name.trim()) {
      removeAuthConfig(editingConfigName);
    }

    const newConfig: AuthConfig = {
      name: form.name.trim(),
      apiBaseUrl: form.apiBaseUrl.trim(),
      clientId: form.clientId.trim(),
      clientSecret: form.clientSecret.trim(),
    };

    addAuthConfig(newConfig);
    const updatedConfigs = loadAuthConfigs();
    setConfigs(updatedConfigs);
    setIsAddingNew(false);
    setIsEditing(false);
    setEditingConfigName(null);
    setActiveConfigName(newConfig.name);
    saveActiveConfigName(newConfig.name);
    onConfigSelected(newConfig);
  }, [
    editingConfigName,
    form.name,
    form.apiBaseUrl,
    form.clientId,
    form.clientSecret,
    isEditing,
    onConfigSelected,
  ]);

  const handleCancelNew = useCallback(() => {
    setIsAddingNew(false);
    setIsEditing(false);
    setEditingConfigName(null);
  }, []);

  const handleDelete = useCallback(
    (name: string) => {
      if (!window.confirm(`Delete configuration "${name}"?`)) return;

      removeAuthConfig(name);
      const updatedConfigs = loadAuthConfigs();
      setConfigs(updatedConfigs);

      if (activeConfigName === name) {
        const newActive = updatedConfigs[0];
        if (newActive) {
          setActiveConfigName(newActive.name);
          saveActiveConfigName(newActive.name);
          onConfigSelected(newActive);
        } else {
          setActiveConfigName('');
        }
      }
    },
    [activeConfigName, onConfigSelected],
  );

  const handleDeleteActive = useCallback(() => {
    if (activeConfigName) handleDelete(activeConfigName);
  }, [activeConfigName, handleDelete]);

  return (
    <StyledRoot>
      <StyledHeading>Environment</StyledHeading>

      {configs.length > 0 && (
        <StyledSection>
          <StyledFieldLabel>Select Environment</StyledFieldLabel>
          <StyledSelectorRow>
            <StyledConfigSelect
              value={activeConfigName}
              onChange={handleChangeSelect}
              disabled={isAddingNew}
              aria-label='Select Environment'
            >
              {configs.map((config) => (
                <option key={config.name} value={config.name}>
                  {config.name}
                </option>
              ))}
            </StyledConfigSelect>
            {activeConfigName && (
              <>
                <button
                  type='button'
                  className='btn-secondary btn-sm'
                  onClick={handleEditActive}
                  disabled={isAddingNew}
                >
                  Edit
                </button>
                <button
                  type='button'
                  className='btn-danger btn-sm'
                  onClick={handleDeleteActive}
                  disabled={isAddingNew}
                >
                  Delete
                </button>
              </>
            )}
          </StyledSelectorRow>
        </StyledSection>
      )}

      {configs.length === 0 && !isAddingNew && (
        <StyledEmptyHint>No configurations saved yet.</StyledEmptyHint>
      )}

      {!isAddingNew && (
        <button type='button' className='btn-secondary btn-sm' onClick={handleAddNew}>
          + Add Configuration
        </button>
      )}

      {isAddingNew && (
        <StyledEditorCard>
          <StyledEditorTitle>
            {isEditing ? 'Edit Configuration' : 'New Configuration'}
          </StyledEditorTitle>
          <StyledFieldStack>
            <StyledField>
              <StyledFieldLabel htmlFor='auth-config-name'>Name</StyledFieldLabel>
              <StyledTextInput
                id='auth-config-name'
                type='text'
                value={form.name}
                onChange={handleChangeName}
                placeholder='e.g. Development'
                disabled={isEditing}
              />
            </StyledField>
            <StyledField>
              <StyledFieldLabel htmlFor='auth-config-api-base-url'>API Base URL</StyledFieldLabel>
              <StyledTextInput
                id='auth-config-api-base-url'
                type='text'
                value={form.apiBaseUrl}
                onChange={handleChangeApiBaseUrl}
                placeholder='https://api.example.com/v1'
              />
            </StyledField>
            <StyledField>
              <StyledFieldLabel htmlFor='auth-config-client-id'>Client ID</StyledFieldLabel>
              <StyledTextInput
                id='auth-config-client-id'
                type='text'
                value={form.clientId}
                onChange={handleChangeClientId}
                placeholder='a410'
              />
            </StyledField>
            <StyledField>
              <StyledFieldLabel htmlFor='auth-config-client-secret'>Client Secret</StyledFieldLabel>
              <StyledTextInput
                id='auth-config-client-secret'
                type='password'
                value={form.clientSecret}
                onChange={handleChangeClientSecret}
                placeholder='asd321'
              />
            </StyledField>
            <StyledButtonRow>
              <button type='button' onClick={handleSaveNew}>
                {isEditing ? 'Update' : 'Save'}
              </button>
              <button type='button' className='btn-ghost' onClick={handleCancelNew}>
                Cancel
              </button>
            </StyledButtonRow>
            {isEditing && (
              <StyledEditHint>
                Update any field and click &quot;Update&quot; to save changes.
              </StyledEditHint>
            )}
          </StyledFieldStack>
        </StyledEditorCard>
      )}
    </StyledRoot>
  );
};

export default React.memo(AuthConfigManager);
export { AuthConfigManager };
